import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import { Autocomplete, TextField } from '@mui/material';
import { Modal } from 'react-bootstrap'
import { TechnologiesModel } from 'app/technologies/model/technologies.model';
import closeModalIcon from 'assets/images/icons/close.svg'
import ITSVG from 'common/helpers/ITSVG';
import SaveButton from 'common/partials/SaveButton';
import { StatusFilter } from 'common/types/status-filter.model';
import { TechnologiesService } from '../../../../technologies/services/technologies.service';
import { MachineModel as Model } from '../../../model/machine.model';
import { TechnologiesLinkService as Service } from '../../../services/technologies-link.service';
import en from '../../../i18n/en-us.json';
import pt from '../../../i18n/pt-br.json';
import es from '../../../i18n/es-es.json';

interface AddTechnologiesModalProps {
  open: boolean;
  handleClose: () => void;
  handleFetchData: () => void;
}

export function AddTechnologiesLink(props: AddTechnologiesModalProps) {
  i18next.addResourceBundle('us', 'translation', en);
  i18next.addResourceBundle('br', 'translation', pt);
  i18next.addResourceBundle('es', 'translation', es);

  const { t } = useTranslation();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);

  const formSchema = Yup.object().shape({
    technologyId: Yup.string().required(t('crud.validators.requiredField')),
  });

  let initialData: Model = {
    id: 0,
    machineId: parseInt(id ?? '0'),
  };

  const [data] = useState<Model>(initialData);
  const [technologies, setTechnologies] = useState<TechnologiesModel[]>([]);

  const formik = useFormik<Model>({
    enableReinitialize: true,
    initialValues: data,
    validationSchema: formSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const body: Model = {
          id: data.id,
          machineId: data.machineId,
          technologyId: values.technologyId,
        };
        const result = await Service.postData(body);
        if (result && !result?.hasErrors) {
          toast.success(`${t('crud.create.successMessage')}`);
        } else {
          toast.error(`${t('crud.create.errorMessage')}`);
        }
      } catch (error) {
        toast.error(`${t('crud.update.errorMessage')}`);
      } finally {
        props.handleClose();
        props.handleFetchData();
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchTechnologies = async () => {
      if (id) {
        const response = await TechnologiesService.getDataList(StatusFilter.Active);
        if (response && !response.hasErrors) {
          setTechnologies(response.data);
        };
      }
    }
    if (props.open) {
      fetchTechnologies();
    }

    return () => {
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return (
    <Modal
      show={props.open}
      onHide={props.handleClose}
      dialogClassName='modal-dialog-centered mw-600px h-auto'
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3>{t('addTechnology')}</h3>
          <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={props.handleClose}>
            <ITSVG path={closeModalIcon} className='svg-icon-1' />
          </div>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <div className="modal-body scroll-y mx-5 my-5">
            <div className="row">
              <div className="col-md-12">
                <label className="required form-label">{t('form.label.name')}</label>
                <Autocomplete
                  id="technologyId"
                  options={technologies}
                  getOptionLabel={option => option.name ? option.name : ""}
                  onChange={(_, value) => formik.setFieldValue("technologyId", value?.id)}
                  onInputChange={(_, value) => {
                    if (!value) {
                      formik.setFieldValue("technologyId", undefined)
                    }
                  }}
                  onBlur={formik.handleBlur}
                  renderInput={(params) =>
                    <TextField
                      {...params}
                      placeholder={`${t('generalMessages.select')}`}
                    />
                  }
                />
                {formik.errors.technologyId && (
                  <div className="mt-3 text-danger fw-bold">{formik.errors.technologyId}</div>
                )}
              </div>
            </div>
          </div>
          <div className="modal-footer d-flex justify-content-end py-6 px-9">
            <button type="button" className="btn btn-light btn-active-light-primary me-2" onClick={props.handleClose}>
              {t('crud.create.buttonCancel')}
            </button>
            <SaveButton type="submit" loading={isLoading} />
          </div>
        </form>
      </div>
    </Modal>
  );
}
